import { Move } from '../types';

export const INITIAL_MOVES: Move[] = [
  {
    id: 'move-backbend',
    slug: 'backbend',
    name: 'Backbend',
    subtitle: 'Learn to arch like a sparkling rainbow with Erika!',
    category: 'flexibility',
    coachFriendId: 'friend-backbend',
    accentColor: '#40E0D0',
    badgeEmoji: '🌈',
    steps: [
      {
        id: 'backbend-step-1',
        number: 1,
        title: 'Warm-Up & Wrist Circles',
        description: 'Wake up your back and wrists so they are super happy and bendy!',
        instructions: [
          'Roll your wrists in circles 10 times each way.',
          'Do the Cat-Cow stretch on your hands and knees (arch up like a cat, then smile like a cow).',
          'Lie on your tummy and push your chest up like a seal (Cobra stretch) for 10 seconds.'
        ],
        safetyTip: 'Never rush warm-ups! If anything pinches or feels uncomfortable, take a rest.',
        coachCue: 'Wrist circles are magic keys that unlock your gymnastics power!',
        iconName: 'activity',
        holdSeconds: 15
      },
      {
        id: 'backbend-step-2',
        number: 2,
        title: 'Bridge From the Floor',
        description: 'Push up into a bridge starting flat on your back on a soft mat.',
        instructions: [
          'Lie on your back with knees bent and feet flat on the floor.',
          'Place your hands flat next to your ears with fingers pointing toward your shoulders.',
          'Push through your hands and feet together to lift your tummy up high into the sky!',
          'Try holding your bridge for 10 seconds with straight arms.'
        ],
        safetyTip: 'Keep your feet flat and look between your hands, not at your toes!',
        coachCue: 'Push the floor away like you are pushing a rocket into space!',
        iconName: 'sparkles',
        holdSeconds: 10
      },
      {
        id: 'backbend-step-3',
        number: 3,
        title: 'Wall Walk-Down Drill',
        description: 'Use a wall to guide you safely backwards step-by-step.',
        instructions: [
          'Stand about an arm’s length away from a clear wall with your feet shoulder-width apart.',
          'Reach your arms up by your ears and look back at the wall.',
          'Place your hands on the wall, and slowly "walk" your hands down the wall one hand at a time.',
          'Walk down as far as feels comfy, then walk your hands back up to standing!'
        ],
        safetyTip: 'Make sure your feet are on a non-slippery mat and take your time walking down.',
        coachCue: 'Your hands are little spider climbers! Spider down, spider up!',
        iconName: 'shield-check',
      },
      {
        id: 'backbend-step-4',
        number: 4,
        title: 'Backbend with an Adult Spotter',
        description: 'Try arching all the way down to a soft mat with an adult supporting your back.',
        instructions: [
          'Stand on a soft mat with your feet wide and stable.',
          'Your adult spotter places one hand firmly under your lower back.',
          'Reach your hands back and let your eyes follow your hands all the way down to the mat.',
          'Once your hands touch the mat, smile and push into your strong bridge!'
        ],
        safetyTip: 'Always have your adult spotter ready before bending backwards!',
        coachCue: 'Keep looking at your hands the whole way down until you see the mat!',
        iconName: 'award',
      },
      {
        id: 'backbend-step-5',
        number: 5,
        title: 'Independent Backbend Champion',
        description: 'Smooth, confident backbend from standing onto a soft mat!',
        instructions: [
          'Stand tall, take a deep breath, and raise your arms right next to your ears.',
          'Push your hips forward gently as your head and arms reach backwards.',
          'Spot the mat with your eyes and plant your hands softly.',
          'Hold your champion bridge for 3 seconds, then gently lower your back down!'
        ],
        safetyTip: 'Only practice on soft gym mats, and celebrate your victory!',
        coachCue: 'You did it, superstar! You are an official Backbend Champion! 🌟',
        iconName: 'trophy',
      }
    ]
  },
  {
    id: 'move-cartwheel',
    slug: 'cartwheel',
    name: 'Cartwheel',
    subtitle: 'Spin sideways like a giant windmill with Chloe!',
    category: 'acrobatics',
    coachFriendId: 'friend-cartwheel',
    accentColor: '#00BFFF',
    badgeEmoji: '🌀',
    steps: [
      {
        id: 'cartwheel-step-1',
        number: 1,
        title: 'Lunge & Mountain Reach',
        description: 'Start in a strong front lunge with arms locked high by your ears.',
        instructions: [
          'Stand with your favorite foot in front and bend your front knee slightly.',
          'Glue your arms to your ears and reach high toward the clouds.',
          'Hold your superhero lunge for 10 seconds to feel your balance!'
        ],
        safetyTip: 'Keep your tummy tight and look straight ahead.',
        coachCue: 'Lunge strong like a superhero ready to fly!',
        iconName: 'activity',
        holdSeconds: 10
      },
      {
        id: 'cartwheel-step-2',
        number: 2,
        title: 'Hand-Hand-Foot-Foot Rhythm',
        description: 'Practice the magical cartwheel counting rhythm on the floor.',
        instructions: [
          'Put two hands on a line or mat: hand 1, hand 2.',
          'Hop your feet over: foot 1, foot 2.',
          'Say the rhythm out loud: "Hand, Hand, Foot, Foot!"'
        ],
        safetyTip: 'Land softly on the balls of your feet.',
        coachCue: 'Sing the rhythm in your head: Hand, Hand, Foot, Foot!',
        iconName: 'sparkles'
      },
      {
        id: 'cartwheel-step-3',
        number: 3,
        title: 'Cartwheel Over a Soft Pillow',
        description: 'Kick your legs over an obstacle to get height and confidence!',
        instructions: [
          'Place a soft foam block or pillow between your hands and landing spot.',
          'Lunge, reach over the block, and hop your legs high over it.',
          'Try to keep your legs straighter on each attempt.'
        ],
        safetyTip: 'Use a soft surface with plenty of open space.',
        coachCue: 'Float your feet over the rainbow pillow!',
        iconName: 'shield-check'
      },
      {
        id: 'cartwheel-step-4',
        number: 4,
        title: 'Full Windmill Cartwheel',
        description: 'Straight legs, pointed toes, and a beautiful landing facing back!',
        instructions: [
          'Big powerful lunge into the floor.',
          'Kick your back leg high into the air with toes pointed.',
          'Spin in a straight line like windmill blades.',
          'Land in a strong back lunge with arms high in victory!'
        ],
        safetyTip: 'Stick your landing with your arms up and chest proud!',
        coachCue: 'Point those toes and stick the landing like an Olympic star!',
        iconName: 'trophy'
      }
    ]
  },
  {
    id: 'move-handstand',
    slug: 'handstand',
    name: 'Handstand',
    subtitle: 'Balance the whole world on your hands with Zara!',
    category: 'strength',
    coachFriendId: 'friend-handstand',
    accentColor: '#9370DB',
    badgeEmoji: '🤸‍♀️',
    steps: [
      {
        id: 'handstand-step-1',
        number: 1,
        title: 'Gorilla Wrists & Plank Power',
        description: 'Warm up your wrists and build steel-strong shoulder muscles.',
        instructions: [
          'Kneel and rock side-to-side on your palms and knuckles.',
          'Hold a strong push-up plank on your hands for 15 seconds.',
          'Squeeze your tummy muscles tight like a statue!'
        ],
        safetyTip: 'Keep your arms straight without bending your elbows.',
        coachCue: 'Strong straight arms like steel pillars!',
        iconName: 'activity',
        holdSeconds: 15
      },
      {
        id: 'handstand-step-2',
        number: 2,
        title: 'Donkey Kicks Drill',
        description: 'Practice lifting your hips above your shoulders with mini hops.',
        instructions: [
          'Hands flat on the mat shoulder-width apart, fingers spread wide.',
          'Take gentle bunny/donkey hops kicking your heels toward your bottom.',
          'Do 5 controlled hops, landing softly every time.'
        ],
        safetyTip: 'Always look at the space between your hands.',
        coachCue: 'Hop like a kangaroo, land like a feather!',
        iconName: 'sparkles'
      },
      {
        id: 'handstand-step-3',
        number: 3,
        title: 'Wall Walk-Up Handstand',
        description: 'Walk your feet up a wall to feel what being upside-down is like.',
        instructions: [
          'Start in a push-up position with feet touching a wall.',
          'Step your feet backwards up the wall while walking hands closer.',
          'Hold your tummy-to-wall handstand for 10 seconds!',
          'Carefully walk your hands forward and feet down.'
        ],
        safetyTip: 'Make sure the wall is clear and step down gently.',
        coachCue: 'Squeeze everything tight! Squeeze your toes, knees, and tummy!',
        iconName: 'shield-check',
        holdSeconds: 10
      },
      {
        id: 'handstand-step-4',
        number: 4,
        title: 'Spotted Handstand Kick-Up',
        description: 'Kick up to a full vertical handstand with an adult holding your legs.',
        instructions: [
          'Lunge and place hands down firmly on the soft mat.',
          'Kick one leg up high; your adult spotter catches your ankles.',
          'Hold your straight pencil line in the air with toes pointed.'
        ],
        safetyTip: 'Never bend your arms! Keep pushing the floor away.',
        coachCue: 'Eyes on your hands, lock your elbows, and hold strong!',
        iconName: 'award'
      },
      {
        id: 'handstand-step-5',
        number: 5,
        title: 'Handstand Champion',
        description: 'Hold a confident handstand on your own and step down gracefully!',
        instructions: [
          'Tall lunge, reach forward, kick up smoothly.',
          'Hold balance for 2 to 3 seconds with straight legs.',
          'Step down one foot at a time into a clean finish!'
        ],
        safetyTip: 'If you ever lose balance, gently tuck your chin or pirouette step down.',
        coachCue: 'You are an upside-down master! Balance is your superpower! 👑',
        iconName: 'trophy'
      }
    ]
  },
  {
    id: 'move-splits',
    slug: 'splits',
    name: 'Splits',
    subtitle: 'Bend like a graceful dancer with Ruby!',
    category: 'flexibility',
    coachFriendId: 'friend-splits',
    accentColor: '#FF7F50',
    badgeEmoji: '💖',
    steps: [
      {
        id: 'splits-step-1',
        number: 1,
        title: 'Butterfly & Knee Flutter',
        description: 'Sit tall and gently open up your hips.',
        instructions: [
          'Sit on your mat and bring the soles of your feet together.',
          'Hold your feet with your hands and sit up as tall as a giraffe.',
          'Gently flutter your knees like butterfly wings for 15 seconds.'
        ],
        safetyTip: 'Keep your back straight and breathe out slowly.',
        coachCue: 'Flutter those butterfly wings softly and smile!',
        iconName: 'sparkles',
        holdSeconds: 15
      },
      {
        id: 'splits-step-2',
        number: 2,
        title: 'Low Runner Lunge Stretch',
        description: 'Stretch your hip flexors on both your right and left sides.',
        instructions: [
          'Step into a deep lunge with back knee resting on a soft mat.',
          'Press your hips forward and hold hands on your front knee.',
          'Hold for 15 seconds, then switch to the other leg!'
        ],
        safetyTip: 'Front knee should stay right above your ankle.',
        coachCue: 'Breathe in deep, exhale and let your muscles relax!',
        iconName: 'activity',
        holdSeconds: 15
      },
      {
        id: 'splits-step-3',
        number: 3,
        title: 'Straight Leg Hamstring Reach',
        description: 'Extend your front leg straight and reach gently toward your toes.',
        instructions: [
          'From your lunge, slide your front leg straight out on your heel.',
          'Flex your front foot and reach your hands toward your ankle.',
          'Hold for 10 seconds with a flat back.'
        ],
        safetyTip: 'Never bounce in a stretch; hold steady and breathe.',
        coachCue: 'Reach with your heart toward your toes!',
        iconName: 'shield-check',
        holdSeconds: 10
      },
      {
        id: 'splits-step-4',
        number: 4,
        title: 'Sliding Split Practice',
        description: 'Slide down gently into your split on a soft mat or with socks!',
        instructions: [
          'Put hands on two yoga blocks, pillows, or the floor for balance.',
          'Slide front foot forward and back foot backwards slowly.',
          'Go as low as comfortable, keep your hips square, and hold for 10 seconds.'
        ],
        safetyTip: 'Stop whenever you feel a good stretch — never push into pain.',
        coachCue: 'Every day you practice, you get one centimeter closer to the floor!',
        iconName: 'award',
        holdSeconds: 10
      },
      {
        id: 'splits-step-5',
        number: 5,
        title: 'Graceful Splits Champion',
        description: 'Hold your split with arms spread out like wings!',
        instructions: [
          'Slide into your best split on the mat.',
          'Raise your hands off the floor and spread them wide with elegance.',
          'Hold for 5 seconds and finish with a big smile!'
        ],
        safetyTip: 'Slide out of the stretch gently after holding.',
        coachCue: 'So beautiful and graceful! You are a Splits Star! ✨',
        iconName: 'trophy'
      }
    ]
  },
  {
    id: 'move-aerial',
    slug: 'aerial',
    name: 'Aerial',
    subtitle: 'The no-hands flying cartwheel with Lily!',
    category: 'acrobatics',
    coachFriendId: 'friend-aerial',
    accentColor: '#98FB98',
    badgeEmoji: '🧚‍♀️',
    steps: [
      {
        id: 'aerial-step-1',
        number: 1,
        title: 'Fast Explosive Cartwheel',
        description: 'Speed and push-off power are the secrets to flying without hands!',
        instructions: [
          'Run two steps into your favorite cartwheel with maximum speed.',
          'Push hard off your front foot as you enter the move.',
          'Land fast and sharp on your feet.'
        ],
        safetyTip: 'Make sure your cartwheels are super solid before starting aerial drills.',
        coachCue: 'Fast like lightning, light like a feather!',
        iconName: 'activity'
      },
      {
        id: 'aerial-step-2',
        number: 2,
        title: 'One-Hand & Late-Hand Cartwheel',
        description: 'Delay putting your hands down as long as possible!',
        instructions: [
          'Do a cartwheel putting only your SECOND hand down.',
          'Next, try putting both hands down only after your legs are already in the air.',
          'Feel how your legs carry the momentum without needing hand support.'
        ],
        safetyTip: 'Practice on thick gym mats.',
        coachCue: 'Wait, wait, wait... then touch! Your legs do the work!',
        iconName: 'sparkles'
      },
      {
        id: 'aerial-step-3',
        number: 3,
        title: 'High Jump Pop Off an Elevation',
        description: 'Step off a low mat or wedge to give you extra air time.',
        instructions: [
          'Step off a soft elevated mat into your takeoff.',
          'Drive your back heel hard toward the ceiling as your chest dips.',
          'Whip your legs around fast in the air.'
        ],
        safetyTip: 'Have a coach or dad nearby to spot your hips.',
        coachCue: 'Explode off the ground like a firework launching into the sky!',
        iconName: 'shield-check'
      },
      {
        id: 'aerial-step-4',
        number: 4,
        title: 'Spotted Aerial with Coach/Dad',
        description: 'Perform the aerial while a spotter holds your hips for safety.',
        instructions: [
          'Take your aggressive step-hop approach.',
          'Your spotter supports your waist and guides your rotation.',
          'Tuck your elbows in and spin clean through the air without touching hands.'
        ],
        safetyTip: 'Keep your chin up and spot your landing mat.',
        coachCue: 'Trust your power! You have wings in the air!',
        iconName: 'award'
      },
      {
        id: 'aerial-step-5',
        number: 5,
        title: 'Pure Flying Aerial Master',
        description: 'Complete hands-free aerial cartwheel stuck on your feet!',
        instructions: [
          'High chest, explosive kick, full flying rotation.',
          'No hands touch the floor — clean flight from start to finish!',
          'Stick the landing with hands in the air and celebrate!'
        ],
        safetyTip: 'Celebrate this elite milestone with high fives and fireworks!',
        coachCue: 'You flew! Pure magic! You are an official Aerial Wizard! 🦄🎉',
        iconName: 'trophy'
      }
    ]
  }
];
